import React from "react";
import { Stars } from "@cmsgov/ds-medicare-gov";
import lowPerformerIcon from "../assets/star-rating-low-performer.svg";
import highPerformerIcon from "../assets/star-rating-high-performer.svg";
import { PlanDetailsHashLink, Tooltip } from ".";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl-phraseapp";
import { sendTealiumEvent, useTranslate, hideStarRatings } from "../helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  StarRatingError,
  StarRating as StarRatingType,
  SearchResultPlan,
  Plan,
  PlanDetailsSection,
} from "../@types";

export interface StarRatingProps {
  includeLabel?: boolean;
  plan?: Plan | SearchResultPlan;
  totalStars?: number;
  stars?: StarRatingType;
  hideTooltip?: boolean;
  highPerforming?: boolean;
  lowPerforming?: boolean;
  planYear: string;
}

const StarRating: React.FunctionComponent<StarRatingProps> = props => {
  const { feEnableStarRatings, mctCurrentPlanYear } = useFlags();
  const t = useTranslate();

  if (
    hideStarRatings(props.planYear, { feEnableStarRatings, mctCurrentPlanYear })
  ) {
    return (
      <div className="StarRating">
        <div className="StarRating__explanationContainer">
          <span className="StarRating__title">{`${t("star_rating.title")}: ${t(
            "coming_soon"
          )}`}</span>
        </div>
      </div>
    );
  }

  const stars = props.stars || { rating: 0 };
  const includeLabel = props.includeLabel !== false;

  const showStars =
    props.stars &&
    !props.highPerforming &&
    !props.lowPerforming &&
    props.stars.error === StarRatingError.STAR_ERROR_NO_ERROR &&
    // temp work around for data issue
    props.stars.rating !== 0;

  const showError =
    (props.stars &&
      props.stars.error !== StarRatingError.STAR_ERROR_NO_ERROR) ||
    // temp work around for data issue
    (props.stars &&
      props.stars.rating === 0 &&
      props.stars.error === StarRatingError.STAR_ERROR_NO_ERROR);

  const StarRatingErr = (): JSX.Element => {
    if (props.stars && props.stars.error) {
      const { error } = props.stars;
      let testId = "";
      let errMessage = "";

      if (error === StarRatingError.STAR_ERROR_PLAN_TOO_NEW) {
        testId = "star-rating-plan-too-new";
        errMessage = t("star_rating.plan_too_new");
      } else if (error === StarRatingError.STAR_ERROR_NOT_ENOUGH_DATA) {
        testId = "star-rating-not-enough-data";
        errMessage = t("star_rating.not_enough_data");
      } else if (error === StarRatingError.STAR_ERROR_NOT_REQUIRED) {
        testId = "star-rating-not-required";
        errMessage = t("star_rating.not_required");
      } else if (error === StarRatingError.STAR_ERROR_NO_DATA) {
        testId = "star-rating-no-data-available";
        errMessage = t("star_rating.no_data_available");
      } else if (error === StarRatingError.STAR_ERROR_PLAN_TOO_SMALL) {
        testId = "star-rating-plan-too-small";
        errMessage = t("star_rating.plan_too_small");
      } else if (error === StarRatingError.STAR_ERROR_NOT_OFFERED) {
        testId = "star-rating-not-offered";
        errMessage = t("star_rating.not_offered");
        // temp work around for data issue
      } else if (
        error === StarRatingError.STAR_ERROR_NO_ERROR &&
        props.stars.rating === 0
      ) {
        testId = "star-rating-plan-too-new";
        errMessage = t("star_rating.plan_too_new");
      }

      return (
        <span
          data-testid={testId}
          className="ds-u-font-size--small ds-u-color--gray StarRating__message"
        >
          {errMessage}
        </span>
      );
    }

    return <></>;
  };

  return (
    <div className="StarRating">
      {props.stars ? (
        <>
          {includeLabel && (
            <div className="StarRating__explanationContainer">
              {props.hideTooltip ? (
                <span className="StarRating__title">
                  {t("star_rating.title")}:
                </span>
              ) : (
                <Tooltip
                  title={t("star_rating.title")}
                  onOpen={(): void => {
                    // istanbul ignore next: tested elsewhere
                    sendTealiumEvent(
                      "Find Plans - Available Plans",
                      "Star Rating"
                    );
                  }}
                  triggerText={t("star_rating.title") + ":"}
                >
                  <p className="ds-u-font-size--small">
                    {t("star_rating.description")}
                  </p>
                  <hr />
                  {props.plan && (
                    <p className="ds-u-font-size--small">
                      <PlanDetailsHashLink
                        section={PlanDetailsSection.STAR_RATINGS}
                        label={t("star_rating.learn_more")}
                        plan={props.plan}
                      />
                    </p>
                  )}
                </Tooltip>
              )}
            </div>
          )}

          {(props.lowPerforming || props.highPerforming) && (
            <div
              className={`StarRating__performer ds-u-font-size--small ${
                includeLabel ? "" : "StarRating__performer--full"
              }`}
            >
              <img
                className="StarRating__performerIcon e2e-star-rating"
                data-stars={stars.rating}
                src={props.lowPerforming ? lowPerformerIcon : highPerformerIcon}
                alt={
                  props.lowPerforming
                    ? t("plan_stars.alt.low_performer")
                    : t("plan_stars.alt.high_performer")
                }
              />
              <span className="mct-c-align-self--end">
                <FormattedHTMLMessage
                  id={
                    props.lowPerforming
                      ? "star_rating.low_performing"
                      : "star_rating.high_performing"
                  }
                  tagName="span"
                />
              </span>
            </div>
          )}

          {showStars && (
            <span
              className="StarRating__stars e2e-star-rating"
              data-stars={stars.rating}
            >
              <Stars number={stars.rating} ariaHidden={true} />
              <span className="sr-only">
                <FormattedMessage
                  id="star_rating.sr_only"
                  values={{
                    totalStars: <strong>{stars.rating}</strong>,
                  }}
                />
              </span>
            </span>
          )}

          {showError && <StarRatingErr />}
        </>
      ) : (
        <span
          data-testid="star-rating-not-available"
          className="ds-u-font-size--small ds-u-color--gray StarRating__message"
        >
          {t("star_rating.not_available")}
        </span>
      )}
    </div>
  );
};

export default StarRating;
